import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { 
  HoverCard, 
  HoverCardContent, 
  HoverCardTrigger 
} from '@/components/ui/hover-card';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Anchor,
  Container,
  Ship,
  Calendar,
  DollarSign,
  MapPin,
  Info,
  Zap,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';

// Auto-refresh interval options (in minutes)
const REFRESH_INTERVALS = [
  { value: '0', label: 'Off' },
  { value: '5', label: '5 min' },
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hour' },
];

/**
 * Terminal Availability Section - Auto-detects terminal and checks availability
 * Supports: APM API, eModal (Trapac, ITS, TTI, PCT, Pier A, Everport), WBCT, and more
 * Shows LFD (Last Free Day), holds, availability status, and other container data
 */
export function TerminalAvailabilitySection({ 
  shipmentId, 
  containerNumber,
  terminalName,
  existingData,
  onDataUpdate 
}) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState(existingData || null);
  const [lastChecked, setLastChecked] = useState(null);
  const [source, setSource] = useState(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState('0'); // minutes, '0' = off
  const autoRefreshRef = useRef(null);
  const previousDataRef = useRef(null); // Store previous data for change detection
  const isAutoRefreshCheck = useRef(false); // Track if current check is from auto-refresh

  // Detect changes between old and new data
  const detectChanges = useCallback((oldData, newData) => {
    if (!oldData || !newData) return [];
    
    const changes = [];
    
    // Check LFD change
    const oldLFD = oldData.lfd;
    const newLFD = newData.lfd;
    if (oldLFD !== newLFD) {
      changes.push({
        type: 'lfd',
        label: 'Last Free Day',
        oldValue: oldLFD || 'Not set',
        newValue: newLFD || 'Not set',
        icon: '📅'
      });
    }
    
    // Check holds change
    const oldHolds = oldData.holds || [];
    const newHolds = newData.holds || [];
    const oldHoldsStr = JSON.stringify(oldHolds.sort());
    const newHoldsStr = JSON.stringify(newHolds.sort());
    
    if (oldHoldsStr !== newHoldsStr) {
      if (newHolds.length > oldHolds.length) {
        changes.push({
          type: 'holds_added',
          label: 'New Holds Added',
          oldValue: `${oldHolds.length} holds`,
          newValue: `${newHolds.length} holds`,
          icon: '🚫'
        });
      } else if (newHolds.length < oldHolds.length) {
        changes.push({
          type: 'holds_removed',
          label: 'Holds Cleared',
          oldValue: `${oldHolds.length} holds`,
          newValue: `${newHolds.length} holds`,
          icon: '✅'
        });
      } else {
        changes.push({
          type: 'holds_changed',
          label: 'Holds Changed',
          oldValue: oldHolds.join(', ') || 'None',
          newValue: newHolds.join(', ') || 'None',
          icon: '⚠️'
        });
      }
    }
    
    // Check availability status change
    const oldAvail = oldData.available || oldData.availability;
    const newAvail = newData.available || newData.availability;
    if (oldAvail !== newAvail) {
      changes.push({
        type: 'availability',
        label: 'Availability Status',
        oldValue: String(oldAvail),
        newValue: String(newAvail),
        icon: newAvail === true || newAvail === 'available' ? '🟢' : '🔴'
      });
    }
    
    // Check demurrage change
    const oldDem = parseFloat(oldData.demurrage_owed) || 0;
    const newDem = parseFloat(newData.demurrage_owed) || 0;
    if (Math.abs(oldDem - newDem) > 0.01) {
      changes.push({
        type: 'demurrage',
        label: 'Demurrage',
        oldValue: `$${oldDem.toFixed(2)}`,
        newValue: `$${newDem.toFixed(2)}`,
        icon: newDem > oldDem ? '💰' : '💵'
      });
    }
    
    return changes;
  }, []);

  // Show change notifications
  const notifyChanges = useCallback((changes, containerNum) => {
    if (changes.length === 0) return;
    
    changes.forEach(change => {
      const message = `${change.icon} ${change.label}: ${change.oldValue} → ${change.newValue}`;
      
      // Use different toast types based on change type
      if (change.type === 'holds_removed' || change.type === 'availability' && change.newValue === 'true') {
        toast.success(message, {
          description: `Container ${containerNum}`,
          duration: 8000
        });
      } else if (change.type === 'holds_added') {
        toast.error(message, {
          description: `Container ${containerNum}`,
          duration: 10000
        });
      } else {
        toast.warning(message, {
          description: `Container ${containerNum}`,
          duration: 8000
        });
      }
    });
  }, []);

  // Polling interval for async tasks
  const [asyncTaskId, setAsyncTaskId] = useState(null);
  const [asyncProgress, setAsyncProgress] = useState(0);
  const asyncPollRef = useRef(null);

  // Poll for async task completion
  const pollAsyncTask = useCallback(async (taskId) => {
    try {
      const response = await api.get(`/terminal-integration/tasks/${taskId}/status`);
      
      const task = response.data;
      setAsyncProgress(task.progress || 0);
      
      if (task.status === 'completed') {
        // Task finished successfully
        clearInterval(asyncPollRef.current);
        asyncPollRef.current = null;
        setAsyncTaskId(null);
        setLoading(false);
        
        const data = task.result?.data;
        
        // Detect changes for auto-refresh
        if (previousDataRef.current && data) {
          const changes = detectChanges(previousDataRef.current, data);
          if (changes.length > 0) {
            notifyChanges(changes, containerNumber);
          }
        }
        
        if (data) {
          previousDataRef.current = { ...data };
        }
        
        setAvailabilityData(data);
        setSource(task.result?.source);
        setLastChecked(new Date().toISOString());
        
        if (onDataUpdate && data) {
          onDataUpdate(data);
        }
        
        toast.success(`Availability updated via ${task.result?.source || task.scraper_id}`);
        
      } else if (task.status === 'failed') {
        // Task failed
        clearInterval(asyncPollRef.current);
        asyncPollRef.current = null;
        setAsyncTaskId(null);
        setLoading(false);
        // iter 294.2 — Long, actionable error message; keep toast open long
        // enough for the user to read hint text like "retry in 30s" or
        // "verify creds in Settings". Also print the reason to console for
        // support tickets.
        const errMsg = task.error || 'Scraper task failed';
        // eslint-disable-next-line no-console
        console.error('[TerminalAvailability] scraper task failed:', {
          scraper_id: task.scraper_id,
          error: task.error,
          error_reason: task.error_reason,
          task_id: task.task_id,
        });
        toast.error(errMsg, {
          duration: 12000,
          description: task.error_reason ? `Reason: ${task.error_reason}` : undefined,
        });
        
      } else {
        // Still running - update progress message
        if (task.progress_message) {
          // Progress is being updated via state
        }
      }
    } catch (error) {
      console.error('Poll error:', error);
    }
  }, [containerNumber, detectChanges, notifyChanges, onDataUpdate, api]);

  // Cleanup async poll on unmount
  useEffect(() => {
    return () => {
      if (asyncPollRef.current) {
        clearInterval(asyncPollRef.current);
      }
    };
  }, []);

  const checkAvailability = useCallback(async (isAutoRefresh = false, useAsync = false) => {
    if (!containerNumber) {
      toast.error('No container number to check');
      return;
    }

    if (!shipmentId) {
      toast.error('Shipment ID required for auto-detection');
      return;
    }

    isAutoRefreshCheck.current = isAutoRefresh;
    setLoading(true);
    setAsyncProgress(0);
    
    try {
      // First, detect which scraper will be used
      const detectResponse = await api.get('/terminal-integration/detect-scraper', {
        params: { terminal_name: terminalName || '' },
      });
      
      const scraperInfo = detectResponse.data;
      const scraperType = scraperInfo.scraper_info?.type;
      const scraperId = scraperInfo.scraper_id;
      
      // Use async endpoint for slow scrapers (emodal, wbct, etc.)
      const slowScrapers = ['emodal', 'wbct', 'pct', 'lbct', 'yti', 'fenix'];
      const shouldUseAsync = useAsync || slowScrapers.includes(scraperId);
      
      if (shouldUseAsync && scraperType === 'scraper') {
        // Use async endpoint for web scrapers
        if (!isAutoRefresh) {
          toast.info(`Starting ${scraperId} scraper... This may take 30-90 seconds.`, {
            duration: 5000
          });
        }
        
        const asyncResponse = await api.post(
          `/terminal-integration/async-check/${shipmentId}`,
          {},
          { validateStatus: () => true }
        );
        
        if (asyncResponse.status >= 400) {
          throw new Error(asyncResponse.data.detail || 'Failed to start async check');
        }
        
        const taskInfo = asyncResponse.data;
        
        // If redirected to sync endpoint (APM), handle that
        if (taskInfo.task_type === 'direct') {
          // Fall through to sync check
        } else {
          // Start polling for async task
          setAsyncTaskId(taskInfo.task_id);
          asyncPollRef.current = setInterval(() => {
            pollAsyncTask(taskInfo.task_id);
          }, 2000); // Poll every 2 seconds
          
          return; // Exit - polling will handle the rest
        }
      }
      
      // Use sync endpoint for fast APIs (APM) or fallback
      const response = await api.post(
        `/terminal-integration/auto-check/${shipmentId}`,
        {},
        {
          validateStatus: () => true,
          timeout: 120000, // 2 minute timeout for sync calls
        }
      );

      const result = response.data;
      
      if (response.status >= 400) {
        throw new Error(result.detail || 'Failed to check availability');
      }

      const data = result.data;
      
      // Detect changes if this is an auto-refresh and we have previous data
      if (isAutoRefresh && previousDataRef.current && data) {
        const changes = detectChanges(previousDataRef.current, data);
        if (changes.length > 0) {
          notifyChanges(changes, containerNumber);
        }
      }
      
      // Store current data for future comparison
      if (data) {
        previousDataRef.current = { ...data };
      }
      
      setAvailabilityData(data);
      setSource(result.source);
      setLastChecked(new Date().toISOString());
      
      if (onDataUpdate && data) {
        onDataUpdate(data);
      }

      // Only show generic toast for manual checks
      if (!isAutoRefresh) {
        if (data) {
          toast.success(`Availability updated via ${result.source}`);
        } else {
          toast.info(result.message || 'No automated tracking available');
        }
      }
    } catch (error) {
      console.error('Availability check error:', error);
      if (!isAutoRefresh) {
        // iter 294.2 — Prefer the backend's detail (which now includes the
        // scraper's specific login reason via _login_reason_to_message)
        // over axios's generic error.message. Users saw "Failed to login
        // to emodal" instead of the real cause because we were reading
        // the wrong property.
        const backendDetail =
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.response?.data?.message;
        toast.error(backendDetail || error.message || 'Failed to check availability', {
          duration: 12000,
        });
      }
    } finally {
      if (!asyncTaskId) {
        setLoading(false);
      }
    }
  }, [containerNumber, shipmentId, terminalName, onDataUpdate, detectChanges, notifyChanges, pollAsyncTask, asyncTaskId, api]);

  // Auto-refresh effect
  useEffect(() => {
    // Clear existing interval
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
      autoRefreshRef.current = null;
    }
    
    // Set up new interval if enabled
    const intervalMinutes = parseInt(autoRefreshInterval);
    if (intervalMinutes > 0 && shipmentId && containerNumber) {
      // Store initial data for comparison
      if (availabilityData && !previousDataRef.current) {
        previousDataRef.current = { ...availabilityData };
      }
      
      autoRefreshRef.current = setInterval(() => {
        checkAvailability(true); // Pass true to indicate auto-refresh
      }, intervalMinutes * 60 * 1000);
      
      // Show toast when auto-refresh is enabled
      toast.info(`Auto-refresh enabled: checking every ${intervalMinutes} minutes`, {
        duration: 3000
      });
    }
    
    // Cleanup on unmount
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefreshInterval, shipmentId, containerNumber, checkAvailability]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Determine status color/icon - Only 2 automatic status changes
  // 1. "Available for Pickup" - when available, no holds
  // 2. "Trouble" - when has holds
  const getStatusDisplay = () => {
    if (!availabilityData) return null;
    
    const isAvailable = availabilityData.available || availabilityData.availability === 'available';
    const hasHolds = availabilityData.holds && availabilityData.holds.length > 0;

    // TROUBLE: Has holds - automatically set
    if (hasHolds) {
      return {
        icon: <AlertTriangle className="w-5 h-5" />,
        label: 'Trouble',
        color: 'bg-[#FA8072] text-slate-900 border-[#FA8072]', // Salmon - matches preset
        description: `${availabilityData.holds.length} hold(s) preventing pickup`,
        autoUpdate: true // Flag to indicate this status should be auto-updated
      };
    }
    
    // AVAILABLE FOR PICKUP: No holds and ready - automatically set
    if (isAvailable && !hasHolds) {
      return {
        icon: <CheckCircle2 className="w-5 h-5" />,
        label: 'Available for Pickup',
        color: 'bg-[#90EE90] text-slate-900 border-[#90EE90]', // Light Green - matches preset
        autoUpdate: true // Flag to indicate this status should be auto-updated
      };
    }
    
    // All other states - just show info, don't auto-update status
    // Show current availability info without suggesting status change
    const state = availabilityData.container_state || availabilityData.availability;

    // iter 294 — Terminal explicitly reports the container is not on an
    // active import cycle. Distinguish this from a real "not available"
    // so users know we DID reach the terminal but it has no data.
    if (state === 'not_on_terminal') {
      return {
        icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
        label: 'Not on terminal',
        color: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        description: availabilityData.error || 'Terminal reports container is not on an active import cycle',
        autoUpdate: false,
      };
    }

    if (state === 'on_vessel' || state === 'INBOUND' || state === 'ADVISED') {
      return {
        icon: <Ship className="w-5 h-5 text-blue-400" />,
        label: 'On Vessel',
        color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        autoUpdate: false // Don't auto-update shipment status
      };
    }
    
    if (state === 'in_yard' || state === 'YARD') {
      return {
        icon: <Container className="w-5 h-5 text-purple-400" />,
        label: 'In Yard',
        color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
        autoUpdate: false
      };
    }
    
    if (state === 'picked_up' || state === 'DEPARTED' || state === 'RETIRED') {
      return {
        icon: <CheckCircle2 className="w-5 h-5 text-slate-400" />,
        label: 'Picked Up',
        color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
        autoUpdate: false
      };
    }
    
    // Default - unknown state, don't auto-update
    return {
      icon: <Clock className="w-5 h-5 text-slate-400" />,
      label: state || 'Checking...',
      color: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      autoUpdate: false
    };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="space-y-4 border border-slate-800 rounded-lg p-4 bg-slate-900/50">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          <h3 className="font-heading font-medium text-white">Terminal Availability</h3>
          <HoverCard>
            <HoverCardTrigger>
              <Info className="w-4 h-4 text-slate-400 cursor-help" />
            </HoverCardTrigger>
            <HoverCardContent className="bg-slate-900 border-slate-700 text-sm w-72">
              <p className="text-slate-300 mb-2">
                Auto-detects terminal and checks availability via:
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• <span className="text-cyan-400">APM API</span> - Pier 400, Mobile, NJ, Miami</li>
                <li>• <span className="text-cyan-400">eModal</span> - Trapac, ITS, TTI, PCT, Pier A, Everport</li>
                <li>• <span className="text-cyan-400">WBCT</span> - West Basin Container Terminal</li>
              </ul>
            </HoverCardContent>
          </HoverCard>
          {source && (
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
              {source}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Auto-refresh selector */}
          <div className="flex items-center gap-1.5">
            <Timer className="w-4 h-4 text-slate-500" />
            <Select value={autoRefreshInterval} onValueChange={setAutoRefreshInterval}>
              <SelectTrigger className="w-[90px] h-8 bg-slate-950 border-slate-700 text-xs">
                <SelectValue placeholder="Auto" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {REFRESH_INTERVALS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-white text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            size="sm"
            variant="outline"
            onClick={() => checkAvailability(false)}
            disabled={loading || !containerNumber || !shipmentId}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            data-testid="check-terminal-availability-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? (asyncTaskId ? `${asyncProgress}%` : 'Checking...') : 'Check'}
          </Button>
        </div>
      </div>

      {/* Async Progress Indicator */}
      {asyncTaskId && (
        <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400 flex items-center gap-2">
              <Timer className="w-4 h-4 animate-pulse text-amber-400" />
              Scraper running in background...
            </span>
            <span className="text-amber-400">{asyncProgress}%</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-amber-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${asyncProgress}%` }}
            />
          </div>
          <p className="text-xs text-slate-500">
            Web scrapers like eModal can take 30-90 seconds. You can navigate away - results will update automatically.
          </p>
        </div>
      )}

      {/* Terminal Info */}
      {terminalName && (
        <div className="flex items-center gap-2 text-sm">
          <Anchor className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">Terminal:</span>
          <span className="text-white">{terminalName}</span>
        </div>
      )}

      {/* No container warning */}
      {!containerNumber && (
        <div className="text-slate-400 text-sm py-4 text-center">
          Enter a container number to check availability
        </div>
      )}

      {/* Data Display */}
      {availabilityData && (
        <div className="space-y-4">
          {/* Status Badge */}
          <div className="flex items-center gap-3 flex-wrap">
            {statusDisplay && (
              <div className="flex flex-col gap-1">
                <Badge className={`${statusDisplay.color} flex items-center gap-1.5 px-3 py-1`}>
                  {statusDisplay.icon}
                  {statusDisplay.label}
                </Badge>
                {statusDisplay.description && (
                  <span className="text-xs text-red-400 ml-1">{statusDisplay.description}</span>
                )}
              </div>
            )}
            
            {(availabilityData.has_appointment || availabilityData.appointment) && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                Has Appointment
              </Badge>
            )}
            
            {lastChecked && (
              <span className="text-xs text-slate-500">
                Last checked: {formatDateTime(lastChecked)}
              </span>
            )}
          </div>

          {/* Key Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* LFD */}
            <div className="bg-slate-800/50 rounded p-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Calendar className="w-3.5 h-3.5" />
                Last Free Day (LFD)
              </div>
              <div className={`font-mono font-medium ${
                availabilityData.lfd ? 'text-amber-400' : 'text-slate-500'
              }`}>
                {formatDate(availabilityData.lfd)}
              </div>
            </div>

            {/* Demurrage */}
            <div className="bg-slate-800/50 rounded p-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <DollarSign className="w-3.5 h-3.5" />
                Demurrage Owed
              </div>
              <div className={`font-mono font-medium ${
                parseFloat(availabilityData.demurrage_owed) > 0 ? 'text-red-400' : 'text-green-400'
              }`}>
                {availabilityData.demurrage_owed 
                  ? `$${parseFloat(availabilityData.demurrage_owed).toLocaleString()}`
                  : '$0.00'
                }
              </div>
            </div>

            {/* Location */}
            <div className="bg-slate-800/50 rounded p-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <MapPin className="w-3.5 h-3.5" />
                Yard Location
              </div>
              <div className="font-mono text-sm text-white truncate" title={availabilityData.yard_location || availabilityData.location}>
                {availabilityData.yard_location || availabilityData.location || '-'}
              </div>
            </div>

            {/* Container Size */}
            <div className="bg-slate-800/50 rounded p-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Container className="w-3.5 h-3.5" />
                Size/Type
              </div>
              <div className="font-mono text-sm text-white">
                {availabilityData.size_type_height || availabilityData.container_type || '-'}
              </div>
            </div>
          </div>

          {/* Holds Section */}
          {availabilityData.holds && availabilityData.holds.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
              <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
                <XCircle className="w-4 h-4" />
                Active Holds ({availabilityData.holds.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {availabilityData.holds.map((hold, idx) => (
                  <Badge key={idx} variant="outline" className="border-red-500/50 text-red-300 text-xs">
                    {typeof hold === 'string' ? hold : hold.name || hold.description || 'Hold'}
                  </Badge>
                ))}
              </div>
              {availabilityData.holds_description && (
                <p className="text-red-300/80 text-xs mt-2">
                  {availabilityData.holds_description}
                </p>
              )}
            </div>
          )}

          {/* Vessel Info */}
          {availabilityData.vessel_name && (
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-1.5">
                <Ship className="w-4 h-4" />
                <span className="text-white">{availabilityData.vessel_name}</span>
              </div>
              {availabilityData.shipping_line && (
                <span>({availabilityData.shipping_line})</span>
              )}
              {availabilityData.vessel_eta && (
                <span>ETA: {formatDateTime(availabilityData.vessel_eta)}</span>
              )}
            </div>
          )}

          {/* Additional Details (collapsed by default) */}
          <details className="text-sm">
            <summary className="text-slate-400 cursor-pointer hover:text-slate-300">
              More Details
            </summary>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-slate-500">Bill of Lading:</span>{' '}
                <span className="text-slate-300">{availabilityData.bill_of_lading || '-'}</span>
              </div>
              <div>
                <span className="text-slate-500">ISO Code:</span>{' '}
                <span className="text-slate-300">{availabilityData.iso_code || '-'}</span>
              </div>
              <div>
                <span className="text-slate-500">Gross Weight:</span>{' '}
                <span className="text-slate-300">{availabilityData.gross_weight_lbs ? `${availabilityData.gross_weight_lbs} lbs` : '-'}</span>
              </div>
              <div>
                <span className="text-slate-500">Discharge Date:</span>{' '}
                <span className="text-slate-300">{formatDateTime(availabilityData.discharge_date)}</span>
              </div>
              <div>
                <span className="text-slate-500">Customs Status:</span>{' '}
                <span className={availabilityData.customs_status === 'released' ? 'text-green-400' : 'text-red-400'}>
                  {availabilityData.customs_status || '-'}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Freight Status:</span>{' '}
                <span className={availabilityData.freight_status === 'released' ? 'text-green-400' : 'text-red-400'}>
                  {availabilityData.freight_status || '-'}
                </span>
              </div>
              {availabilityData.hazardous && (
                <div className="col-span-2">
                  <span className="text-amber-500">⚠ Hazardous:</span>{' '}
                  <span className="text-amber-300">{availabilityData.hazardous}</span>
                </div>
              )}
            </div>
          </details>
        </div>
      )}

      {/* Empty state */}
      {!availabilityData && containerNumber && (
        <div className="text-slate-500 text-sm py-4 text-center">
          Click &ldquo;Check Availability&rdquo; to fetch data from terminal
        </div>
      )}
    </div>
  );
}

// Keep APMAvailabilitySection as alias for backward compatibility
export const APMAvailabilitySection = TerminalAvailabilitySection;

export default TerminalAvailabilitySection;
